import React, { useState } from 'react'
import { Formik, Form, FieldArray } from 'formik'
import {
  CurrencyInputField,
  InputField,
  InputSelectOptions,
  LoaderButton
} from 'components/molecules'
import PropTypes from 'prop-types'
import { Alert, Col, FormGroup, Input, Label, Row } from 'reactstrap'
import { CONDITION, RECEIVED_STATUS, STATUS } from 'domains/fast-sell-product/constants'
import SellerSelectSearchField from 'domains/seller/molecules/select-search-field/seller-select-search-field'
import fastSellProductCreateSchema from './fast-sell-product-form-schema'
import { ANGLE_LABELS, ANGLE_PLACEHOLDER_IMAGES } from 'domains/fast-sell-image/constants'
import FastSellImageApi from 'api/v2/admins/fast-sell-image-api'
import Resizer from 'react-image-file-resizer'
import {
  FastSellProductBrandSelectField,
  FastSellProductCategorySelectField,
  FastSellProductDetailCheckboxField
} from 'domains/fast-sell-product/components'
import { FastSellImageUploadFIeld } from 'domains/fast-sell-image/components'
import fastSellProductFormInitialValues from './fast-sell-product-form-initial-values'

const propTypes = {
  fastSellProduct     : PropTypes.object,
  onClickSubmit       : PropTypes.func,
  disableSubmitButton : PropTypes.bool,
  formType            : PropTypes.string
}

const defaultProps = {
  onClickSubmitForm   : () => { },
  disableSubmitButton : false,
  formType            : 'create'
}

const FastSellProductForm = ({
  fastSellProduct,
  onClickSubmit,
  disableSubmitButton,
  formType
}) => {
  const [ showOtherDetailField, setShowOtherDetailField ] = useState(false)
  const [ isLoading, setIsLoading ]                       = useState({
    imageIndex: false
  })

  const isUpdateForm = formType === 'update'

  const handleClickKelengkapanCheckBox = (
    checkBox,
    event,
    formik
  ) => {
    const isChecked                     = event.target.checked == true
    const detailsExist                  = formik.values.details.length > 0
    const isDetailsExistAndCheckedFalse = !isChecked && detailsExist


    if (isChecked) {
      const oldValue = formik.values.details
      formik.setFieldValue('details', [ ...oldValue, checkBox ])
    }
    else if (isDetailsExistAndCheckedFalse) {
      const newValue = formik.values.details.filter(function (item){
        return item !== checkBox
      })
      formik.setFieldValue('details', newValue)
    }
  }

  const handleEnterOtherDetails = (
    event,
    values,
    setFieldValue
  ) => {
    if (event.key === 'Enter') {
      const oldValue = values.details
      setFieldValue('details', [ ...oldValue, values.otherDetail ])
      setFieldValue('otherDetail', '')
    }
  }

  const handleClickOtherCheckBox = (event) => {
    if (event.target.checked == true) {
      setShowOtherDetailField(true)
    }
    else if (event.target.checked == false) {
      setShowOtherDetailField(false)
    }
  }

  const initializeFormData = (
    file,
    angle,
    isCover,
    order,
    formik
  ) => {
    const formData = new FormData()
    formData.append('fast_sell_image[image]', file)
    formData.append('fast_sell_image[angle]', angle)
    formData.append('seller_id', formik.values.seller_id)
    formData.append('fast_sell_image[is_cover]', isCover)
    formData.append('fast_sell_image[order]', order)
    return formData
  }

  const handleDropImage = async (
    files,
    image,
    index,
    arrayHelpers,
    formik
  ) => {
    setIsLoading((prevState) => ({
      ...prevState,
      imageIndex: index
    }))
    const resizedFilePromises = files.map((file) => resizeFile(file))
    const resizedFiles        = await Promise.all(resizedFilePromises)
      .then((resolvedPromise) => resolvedPromise)

    // Upload resized files
    resizedFiles.map((file) => {
      const formData = initializeFormData(
        file,
        image?.angle,
        image?.is_cover,
        image?.order,
        formik
      )
      FastSellImageApi.create({ formData })
        .then((response) => {
          const image = response.data.fast_sell_image
          arrayHelpers.replace(index, image)
        })
        .catch((error) => console.log(error))

      setIsLoading((prevState) => ({
        ...prevState,
        imageIndex: false
      }))
    })
  }

  /**
   * Resize the file to max 1280px on the width or height maintaining aspect ratio
   * @param {file} file - selected file
   * @returns {Promise}
   */
  const resizeFile = (file) => (
    new Promise((resolve) => {
      Resizer.imageFileResizer(
        file,
        1280,
        1280,
        'JPEG',
        100,
        0,
        (uri) => {
          resolve(uri)
        },
        'file',
        1280
      )
    })
  )

  const handleClickRemoveImage = async (
    angleValue,
    index,
    arrayHelpers,
    formik
  ) => {
    // remove from formik via arrayHelpers
    const placeholderImage = { angle: angleValue }
    arrayHelpers.replace(index, placeholderImage)

    const currentImage = formik.values.images[index]

    await FastSellImageApi.destroy({ imageId: currentImage.id })
      .then((response) => {
        console.log(response)
      })
      .catch((error) => console.log(error))
  }

  const ImageGuideline = () => (
    <Alert color='primary'>
      <p>
        <i className={ 'fas fa-info-circle' }/> { 'Pilih ' }
        <strong>{ 'Seller' }</strong>{ ' sebelum mengupload foto.' }
      </p>
      <p>
        <i className={ 'fas fa-info-circle' }/> { 'Foto/gambar yang menggunakan format ' }
        <strong>{ 'iOS (HEIC/HEIF)' }</strong>{ ' tidak dapat dibaca oleh semua aplikasi browser.' }
      </p>
      <p>{ 'Untuk itu, kamu dapat mengikuti langkah berikut agar foto/gambar kamu dapat di upload dengan sempurna:' }</p>
      <ol type='1'>
        <li>{ 'Ubah format foto/gambar kamu menggunakan ' }
          <a
            className='text-secondary'
            style={{ textDecoration: 'underline' }}
            href='https://www.tinypng.com'
            target="_blank"
            rel='noreferrer'
          >
            { 'link berikut' }
          </a>
        </li>
        <li>{ 'Download foto/gambar dengan format baru dan upload ke formulir.' }</li>
      </ol>
    </Alert>
  )

  const showSubmitButton = disableSubmitButton || isUpdateForm

  return (
    <Formik
      initialValues={ fastSellProductFormInitialValues(fastSellProduct) }
      enableReinitialize={ true }
      validationSchema={ fastSellProductCreateSchema }
      onSubmit={ onClickSubmit }
    >
      {
        (formik) => (
          <Form>
            <Row>
              <Col md={ 6 }>
                {
                  isUpdateForm && (
                    <>
                      <InputField
                        type={ 'select' }
                        name={ 'status' }
                        label={ 'Status' }
                        required={ isUpdateForm }
                        formText={ 'Status dari Produk (Admin akan mengupdate status sesuai dengan keadaan produk saat ini)' }
                      >
                        <InputSelectOptions
                          options={ STATUS }
                        />
                      </InputField>

                      <InputField
                        type={ 'select' }
                        name={ 'received_status' }
                        label={ 'Received Status' }
                        required={ isUpdateForm }
                        formText={ 'Status penerimaan (apakah produk sudah diterima Tim Tinkerlust)' }
                      >
                        <InputSelectOptions
                          options={ RECEIVED_STATUS }
                        />
                      </InputField>
                    </>
                  )
                }
                <SellerSelectSearchField
                  name='seller_id'
                  label='Seller'
                  required={ true }
                  formText={ 'Seller yang memiliki produk ini' }
                />

                <FastSellProductCategorySelectField
                  inline
                  name={ 'product_category_id' }
                  label={ 'Kategori' }
                  required={ true }
                  formText={ 'Kategori dari produk ini (contoh: Tas)' }
                  dataType={ 'const' }
                />

                <FastSellProductBrandSelectField
                  name={ 'product_brand_id' }
                  label={ 'Brand' }
                  required={ true }
                  formText={ 'Brand dari produk ini (contoh: Balenciaga)' }
                />

                <InputField
                  type={ 'select' }
                  name={ 'condition' }
                  label={ 'Kondisi' }
                  required={ true }
                  formText={ 'Kondisi dari produk ini' }
                >
                  <InputSelectOptions
                    options={ CONDITION }
                  />
                </InputField>

                <InputField
                  type={ 'input' }
                  name={ 'name' }
                  label={ 'Nama' }
                  required={ true }
                  formText={ 'Nama dari produk' }
                />

                <InputField
                  type={ 'input' }
                  name={ 'serial_code' }
                  label={ 'Serial Code' }
                  formText={ 'Serial code yang dimiliki oleh produk (biasanya ada di tag/label)' }
                />
              </Col>

              <Col md={ 6 }>
                <InputField
                  type={ 'input' }
                  name={ 'details' }
                  label={ 'Kelengkapan' }
                  disabled={ true }
                  formText={ 'Kelengkapan dari produk. Silahkan pilih checkbox (klik lainnya jika kelengkapan tidak tersedia di pilihan)' }
                />

                <FastSellProductDetailCheckboxField
                  className={ 'my-3' }
                  onClickCheckBox={
                    (checkBox, event) => handleClickKelengkapanCheckBox(
                      checkBox,
                      event,
                      formik
                    )
                  }
                  details = { formik.values.details }
                />

                <FormGroup
                  check
                  inline
                  className='mb-3'
                >
                  <Input
                    type="checkbox"
                    onChange={ handleClickOtherCheckBox }
                  />
                  <Label check>{ 'Lainnya' }</Label>
                </FormGroup>

                {
                  showOtherDetailField && (
                    <>
                      <InputField
                        type={ 'input' }
                        name={ 'otherDetail' }
                        label={ 'Kelengkapan (lainnya)' }
                        // prevent submitting formik when clicking enter
                        onKeyPress={ (e) => { e.key == 'Enter' && e.preventDefault() } }
                        onKeyUp={ (event) => {
                          handleEnterOtherDetails(
                            event,
                            formik.values,
                            formik.setFieldValue
                          )
                        } }
                      />
                      <Alert color={ 'primary' }>
                        <i className='fas fa-info-circle mr-3' />
                        { 'Klik tombol `Enter` untuk menambahkan kelengkapan lainnya' }
                      </Alert>
                    </>
                  )
                }

                <InputField
                  formText={ 'Tahun Anda membeli produk ini (jika ada).' }
                  name={ 'year_purchased' }
                  type={ 'number' }
                  pattern="[0-9]*"
                  label={ 'Tahun Pembelian' }
                />

                <CurrencyInputField
                  name={ 'price_offer' }
                  label={ 'Harga' }
                  required={ true }
                  formText={ 'Harga yang ditawarkan pertama kali oleh Seller' }
                />
              </Col>
            </Row>

            <InputField
              type={ 'textarea' }
              rows={ 2 }
              name={ 'note' }
              label={ 'Additional Information' }
              formText={ 'Informasi tambahan dari Seller yang ingin disampaikan' }
            />

            <h4>{ 'Unggah foto produk' }</h4>

            <ImageGuideline />

            <Row className='mb-3'>
              <FieldArray name='images'>
                {
                  (arrayHelpers) => (
                    formik.values.images.map((image, index) => (
                      <Col
                        xs={ 6 }
                        sm={ 2 }
                        md={ 2 }
                        lg={ 2 }
                        key={ `col-image-angle-${index}` }
                        className='py-2'
                      >
                        <FastSellImageUploadFIeld
                          name={ `images[${index}]` }
                          isMultiple={ false }
                          isLoading={ isLoading.imageIndex === index }
                          placeholderImg={ ANGLE_PLACEHOLDER_IMAGES[ image.angle ] }
                          disabled={ !formik.values.seller_id }
                          onDrop={
                            (file) => handleDropImage(
                              file,
                              image,
                              index,
                              arrayHelpers,
                              formik
                            )
                          }
                          onClickRemove={
                            (removedImage) => handleClickRemoveImage(
                              removedImage.angle,
                              index,
                              arrayHelpers,
                              formik
                            )
                          }
                        />
                        <div className='small text-capitalize'>
                          { ANGLE_LABELS[ image.angle ] }
                        </div>
                      </Col>
                    ))
                  )
                }
              </FieldArray>
            </Row>

            {
              showSubmitButton && (
                <LoaderButton
                  color='primary'
                  className='float-right'
                  type='submit'
                  disabled={ formik.isSubmitting }
                  isLoading={ formik.isSubmitting }
                  text='Submit'
                />
              )
            }
          </Form>
        )
      }
    </Formik>
  )
}

FastSellProductForm.propTypes    = propTypes
FastSellProductForm.defaultProps = defaultProps

export default FastSellProductForm
